import React from 'react';
import { ArrowRight, Shield, User } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SectionAccordionItem } from '../cards';

export interface DetailsTabCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
}

export interface DetailsTabProps {
  roleName: string;
  onRoleNameChange: (value: string) => void;
  roleDescription: string;
  onRoleDescriptionChange: (value: string) => void;
  isActive: boolean;
  onIsActiveChange: (value: boolean) => void;
  isSystem: boolean;
  nameError: string;
  usersCount: number;
  totalPermissionsCount: number;
  categories: DetailsTabCategory[];
  getCategorySummaryCount: (categoryId: string) => { enabled: number; total: number };
  onViewUsers: () => void;
}

export const DetailsTab: React.FC<DetailsTabProps> = ({
  roleName,
  onRoleNameChange,
  roleDescription,
  onRoleDescriptionChange,
  isActive,
  onIsActiveChange,
  isSystem,
  nameError,
  usersCount,
  totalPermissionsCount,
  categories,
  getCategorySummaryCount,
  onViewUsers,
}) => (
  <Accordion type="multiple" defaultValue={['role-details', 'role-summary']} className="space-y-3">

    {/* Role Details */}
    <SectionAccordionItem value="role-details" icon={<User className="h-5 w-5" />} title="Role Details" description="Configure the basic information for this role">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

        {/* Role Name */}
        <div className="flex flex-col gap-2 p-4 rounded-lg border bg-background">
          <Label htmlFor="role-name" className="text-sm font-medium">
            Role Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="role-name"
            value={roleName}
            onChange={(e) => onRoleNameChange(e.target.value)}
            placeholder="e.g., Project Manager"
            disabled={isSystem}
            className={nameError ? 'border-destructive' : ''}
          />
          {nameError
            ? <p className="text-xs text-destructive">{nameError}</p>
            : <p className="text-xs text-muted-foreground">{isSystem ? 'Platform role names cannot be changed' : 'The display name for this role'}</p>
          }
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
          <div className="flex-1 min-w-0">
            <Label htmlFor="role-active" className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Enable this role to make it available for assignment to users</p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0">
                  <Checkbox
                    id="role-active"
                    checked={isActive}
                    onCheckedChange={(checked) => onIsActiveChange(!!checked)}
                    disabled={isSystem}
                  />
                </span>
              </TooltipTrigger>
              {isSystem && (
                <TooltipContent side="top" align="end" className="max-w-[220px] text-center">
                  <p>Platform roles must remain active as they are required for system functionality</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Description — spans full width */}
        <div className="flex flex-col gap-2 p-4 rounded-lg border bg-background lg:col-span-2">
          <Label htmlFor="role-description" className="text-sm font-medium">Description</Label>
          <Textarea
            id="role-description"
            value={roleDescription}
            onChange={(e) => onRoleDescriptionChange(e.target.value)}
            placeholder="Describe what this role can do..."
            rows={3}
          />
          <p className="text-xs text-muted-foreground">A brief description of what this role can do</p>
        </div>
      </div>
    </SectionAccordionItem>

    {/* Summary */}
    <SectionAccordionItem value="role-summary" icon={<Shield className="h-5 w-5" />} title="Summary" description="Overview of enabled permissions across all categories">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <button
          onClick={onViewUsers}
          className="rounded-lg border bg-background p-4 text-left hover:bg-accent hover:border-primary transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <p className="text-3xl font-bold">{usersCount}</p>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Users assigned to this role</p>
          <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view users →</p>
        </button>
        <div className="rounded-lg border bg-background p-4">
          <p className="text-3xl font-bold">{totalPermissionsCount}</p>
          <p className="text-sm text-muted-foreground mt-1">Total permissions enabled</p>
        </div>
        <div className="rounded-lg border bg-background p-4">
          <div className="flex items-center gap-2">
            <div className={`h-3 w-3 rounded-full ${isActive ? 'bg-success-500' : 'bg-neutral-400'}`} />
            <p className="text-3xl font-bold">{isActive ? 'Active' : 'Inactive'}</p>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Role status</p>
        </div>
      </div>
      <div className="p-4 rounded-lg border bg-background space-y-2">
        <p className="text-sm font-medium mb-2">Permissions by Category:</p>
        {categories.map((category) => {
          const summaryCount = getCategorySummaryCount(category.id);
          const percentage = summaryCount.total > 0 ? Math.round((summaryCount.enabled / summaryCount.total) * 100) : 0;
          return (
            <div key={category.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
              <div className="flex items-center gap-2">
                {category.icon}
                <span>{category.name}</span>
              </div>
              <span className="text-muted-foreground">{summaryCount.enabled}/{summaryCount.total} ({percentage}%)</span>
            </div>
          );
        })}
      </div>
    </SectionAccordionItem>
  </Accordion>
);
